import { useState, useEffect } from "react";
import { Card } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import {
  Globe,
  Search,
  Users,
  TrendingUp,
  BarChart3,
  CheckCircle2,
  Loader2,
  Sparkles,
  Target,
  Lightbulb,
  Trophy,
  AlertTriangle
} from "lucide-react";

const CONFIG = {
  brandGreen: "#07926b",
};

// Progressive insights that appear over time
const PROGRESSIVE_INSIGHTS = [
  { delay: 5000, icon: Globe, text: "Scanning website structure and content...", type: "status" },
  { delay: 12000, icon: Search, text: "Analyzing messaging clarity and value proposition...", type: "status" },
  { delay: 20000, icon: Users, text: "Evaluating social proof and credibility signals...", type: "status" },
  { delay: 30000, icon: TrendingUp, text: "Researching market positioning and competitor landscape...", type: "status" },
  { delay: 45000, icon: BarChart3, text: "Assessing conversion optimization elements...", type: "status" },
  { delay: 60000, icon: Target, text: "Analyzing call-to-action effectiveness...", type: "status" },
  { delay: 80000, icon: Sparkles, text: "Generating personalized insights...", type: "status" },
  { delay: 100000, icon: CheckCircle2, text: "Finalizing your comprehensive report...", type: "status" },
];

// Educational stats/tips that rotate
const EDUCATIONAL_CONTENT = [
  { stat: "73%", text: "of mid-market companies lose revenue due to unclear GTM positioning" },
  { stat: "2.3x", text: "higher conversion rates for companies with strong social proof" },
  { stat: "47%", text: "of B2B buyers consume 3-5 pieces of content before engaging sales" },
  { stat: "68%", text: "of customers leave due to perceived indifference, not price" },
  { stat: "91%", text: "of B2B buyers prefer interactive content over static content" },
  { stat: "5-7", text: "touchpoints needed on average before a prospect converts" },
];

// Analysis checklist items
const ANALYSIS_CHECKLIST = [
  { id: 1, label: "Website Structure", timeToComplete: 8000 },
  { id: 2, label: "Messaging Clarity", timeToComplete: 18000 },
  { id: 3, label: "Social Proof", timeToComplete: 30000 },
  { id: 4, label: "Market Positioning", timeToComplete: 50000 },
  { id: 5, label: "Conversion Elements", timeToComplete: 70000 },
  { id: 6, label: "External Signals", timeToComplete: 100000 },
  { id: 7, label: "AI Analysis", timeToComplete: 140000 },
  { id: 8, label: "Report Generation", timeToComplete: 170000 },
];

export default function AnalysisProgress({ 
  companyName, 
  pollingStatus, 
  onPrediction,
  startTime 
}) {
  const [visibleInsights, setVisibleInsights] = useState([]);
  const [currentEducation, setCurrentEducation] = useState(0);
  const [predictedScore, setPredictedScore] = useState(50);
  const [hasPredicted, setHasPredicted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [completedChecklist, setCompletedChecklist] = useState([]);

  // Track elapsed time
  useEffect(() => {
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setElapsedTime(elapsed);
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  // Progressive insights
  useEffect(() => {
    const timers = PROGRESSIVE_INSIGHTS.map((insight, index) => {
      return setTimeout(() => {
        setVisibleInsights(prev => [...prev, index]);
      }, insight.delay);
    });
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Rotate educational content
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentEducation(prev => (prev + 1) % EDUCATIONAL_CONTENT.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  // Update completed checklist based on elapsed time
  useEffect(() => {
    const completed = ANALYSIS_CHECKLIST
      .filter(item => elapsedTime >= item.timeToComplete)
      .map(item => item.id);
    setCompletedChecklist(completed);
  }, [elapsedTime]);

  const handlePrediction = () => {
    setHasPredicted(true);
    if (onPrediction) {
      onPrediction(predictedScore);
    }
  };

  const progressPercentage = Math.min((elapsedTime / 180000) * 100, 95);
  const currentEdu = EDUCATIONAL_CONTENT[currentEducation];

  return (
    <div className="space-y-6">
      {/* Main Progress Card */}
      <Card className="p-6 bg-white shadow-xl border-0">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-3" 
               style={{ backgroundColor: 'rgba(7, 146, 107, 0.1)' }}>
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: CONFIG.brandGreen }} />
            <span className="text-sm font-medium" style={{ color: CONFIG.brandGreen }}>
              Analyzing {companyName || "your website"}
            </span>
          </div>
          
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Deep Research in Progress
          </h2>
          <p className="text-sm text-slate-500">
            Our AI is scanning your website and researching external signals
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-600">Progress</span>
            <span className="font-medium" style={{ color: CONFIG.brandGreen }}>
              {Math.round(progressPercentage)}%
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
          <p className="text-xs text-slate-400 mt-1 text-center">
            Estimated time remaining: {Math.max(0, Math.ceil((180000 - elapsedTime) / 1000))}s
          </p>
        </div>

        {/* Analysis Checklist */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          {ANALYSIS_CHECKLIST.map((item) => {
            const isComplete = completedChecklist.includes(item.id);
            const isActive = !isComplete && elapsedTime >= item.timeToComplete - 10000;
            return (
              <div 
                key={item.id}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-all duration-500 ${
                  isComplete 
                    ? 'bg-green-50 text-green-700' 
                    : isActive 
                      ? 'bg-amber-50 text-amber-700' 
                      : 'bg-slate-50 text-slate-400'
                }`}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                ) : isActive ? (
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />
                )}
                <span className={isComplete ? 'font-medium' : ''}>{item.label}</span>
              </div>
            );
          })}
        </div>

        {/* Latest Status */}
        {visibleInsights.length > 0 && (
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-start gap-3 animate-fade-in">
              {(() => {
                const latestIndex = visibleInsights[visibleInsights.length - 1];
                const latest = PROGRESSIVE_INSIGHTS[latestIndex];
                const Icon = latest.icon;
                return (
                  <>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                         style={{ backgroundColor: 'rgba(7, 146, 107, 0.1)' }}>
                      <Icon className="w-4 h-4" style={{ color: CONFIG.brandGreen }} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-700">{latest.text}</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </Card>

      {/* Score Prediction Game */}
      {!hasPredicted ? (
        <Card className="p-6 bg-gradient-to-br from-slate-50 to-white shadow-lg border-0">
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
                 style={{ backgroundColor: 'rgba(7, 146, 107, 0.1)' }}>
              <Trophy className="w-6 h-6" style={{ color: CONFIG.brandGreen }} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">
              Predict Your Score!
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              How would you rate your current go-to-market strategy?
            </p>
          </div>

          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-500 mb-2">
              <span>Needs Work</span>
              <span className="text-2xl font-bold" style={{ color: CONFIG.brandGreen }}>
                {predictedScore}
              </span>
              <span>Excellent</span>
            </div>
            <Slider
              value={[predictedScore]}
              onValueChange={(value) => setPredictedScore(value[0])}
              min={0}
              max={100}
              step={1}
              className="mt-2"
            />
          </div>

          <Button
            onClick={handlePrediction}
            className="w-full text-white font-medium"
            style={{ backgroundColor: CONFIG.brandGreen }}
          >
            Lock In My Prediction
          </Button>
          
          <p className="text-xs text-slate-400 text-center mt-2">
            See how close you are when results arrive!
          </p>
        </Card>
      ) : (
        <Card className="p-4 bg-gradient-to-br from-green-50 to-white shadow-lg border border-green-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Prediction Locked: {predictedScore}/100</p>
              <p className="text-sm text-slate-500">Let's see how close you are!</p>
            </div>
          </div>
        </Card>
      )}

      {/* Educational Content */}
      <Card className="p-5 bg-slate-800 text-white shadow-lg border-0">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: CONFIG.brandGreen }}>
            <Lightbulb className="w-5 h-5 text-white" />
          </div>
          <div className="animate-fade-in" key={currentEducation}>
            <p className="text-3xl font-bold mb-1" style={{ color: CONFIG.brandGreen }}>
              {currentEdu.stat}
            </p>
            <p className="text-sm text-slate-300">
              {currentEdu.text}
            </p>
          </div>
        </div>
      </Card>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
